import React, { useState, useEffect } from "react";
import {
  User as UserIcon,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  CheckCircle,
  Edit3,
  X,
  Trophy,
  Users,
  FileText,
  BarChart3,
  AlertTriangle,
  Clock,
  Upload,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  getDocs,
  limit,
} from "firebase/firestore";
import { safeToDate } from "../../utils/SafeToDate";

// HR Gamification Components
import RecruiterLevelProgressBar from "../../components/Gamification/RecruiterLevelProgressBar";
import RecruiterStreakCounter from "../../components/Gamification/RecruiterStreakCounter";
import HRAchievementGrid from "../../components/Gamification/HRAchievementGrid";
// HR Gamification Constants
import {
  getHRLevelByXP,
  HR_XP_POINTS,
  HR_ACHIEVEMENTS
} from "../../constants/hrGamification";

interface CompanyStats {
  totalJobs: number;
  totalTests: number;
  totalCandidates: number;
  interviewsCompleted: number;
}

const HRProfile: React.FC = () => {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);
  const [profileData, setProfileData] = useState<any>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStats>({
    totalJobs: 0,
    totalTests: 0,
    totalCandidates: 0,
    interviewsCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // HR Gamification State
  const [showAllAchievements, setShowAllAchievements] = useState(false);
  const [hrGamificationData, setHRGamificationData] = useState({
    totalXP: 0,
    currentLevel: 1,
    currentLevelName: 'Junior Recruiter',
    currentStreak: 0,
    longestStreak: 0,
    lastActivityDate: new Date(),
    streakFreezeTokens: 0,
    achievements: [] as any[]
  });
  const [gamificationLoading, setGamificationLoading] = useState(true);

  // Load profile data from Firebase
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return;

      try {
        // Get HR profile data from Firebase
        const profileDoc = await getDoc(doc(db, "hrProfiles", user.id));

        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setProfileData({
            name: data.name || user?.name || "HR Manager",
            email: data.email || user?.email || "",
            phone: data.phone || "+1 (555) 123-4567",
            location: data.location || "Location not provided",
            company: data.company || "Company Name",
            department: data.department || "Human Resources",
            title: data.title || "HR Manager",
            bio: data.bio || "Professional HR manager focused on finding top talent.",
            joinedDate:
              safeToDate(user?.createdAt)?.toLocaleDateString('en-CA') ||
              new Date().toLocaleDateString('en-CA'),
          });
        } else {
          // No profile data found, create default data
          const defaultData = {
            name: user?.name || "HR Manager",
            email: user?.email || "",
            phone: "+1 (555) 123-4567",
            location: "Location not provided",
            company: "Company Name",
            department: "Human Resources",
            title: "HR Manager",
            bio: "Professional HR manager focused on finding top talent.",
            joinedDate:
              safeToDate(user?.createdAt)?.toLocaleDateString('en-CA') ||
              new Date().toLocaleDateString('en-CA'),
          };

          // Save default data to Firebase
          await setDoc(doc(db, "hrProfiles", user.id), {
            ...defaultData,
            createdAt: new Date(),
          });

          setProfileData(defaultData);
        }
      } catch (error) {
        // Fallback to default data
        const defaultData = {
          name: user?.name || "HR Manager",
          email: user?.email || "",
          phone: "+1 (555) 123-4567",
          location: "Location not provided",
          company: "Company Name",
          department: "Human Resources",
          title: "HR Manager",
          bio: "Professional HR manager focused on finding top talent.",
          joinedDate: "2024-12-15",
        };

        setProfileData(defaultData);
      } finally {
        setLoading(false);
      }
    };

    loadProfileData();
  }, [user, db]);

  // Fetch company stats
  useEffect(() => {
    if (!user) return;


    // Count total jobs posted
    const jobsQuery = query(
      collection(db, "hrJobs"),
      where("hrId", "==", user.id)
    );

    // Count total tests created
    const testsQuery = query(
      collection(db, "hrTests"),
      where("hrId", "==", user.id)
    );

    // Get candidates query
    const candidatesQuery = query(
      collection(db, "jobseekerResults"),
      where("hrId", "==", user.id)
    );

    // Set up real-time listeners
    const unsubscribeJobs = onSnapshot(jobsQuery, (snapshot) => {
      setCompanyStats(prev => ({ ...prev, totalJobs: snapshot.size }));
    }, (error) => {
    });

    const unsubscribeTests = onSnapshot(testsQuery, (snapshot) => {
      setCompanyStats(prev => ({ ...prev, totalTests: snapshot.size }));
    }, (error) => {
    });

    const unsubscribeCandidates = onSnapshot(candidatesQuery, (snapshot) => {
      const uniqueCandidates = new Set(snapshot.docs.map(doc => doc.data().jobSeekerId));
      setCompanyStats(prev => ({ ...prev, totalCandidates: uniqueCandidates.size }));
    }, (error) => {
    });

    // Get interviews completed query
    const interviewsQuery = query(
      collection(db, "jobseekerResults"),
      where("hrId", "==", user.id),
      where("hasCompletedInterview", "==", true)
    );

    const unsubscribeInterviews = onSnapshot(interviewsQuery, (snapshot) => {
      setCompanyStats(prev => ({ ...prev, interviewsCompleted: snapshot.size }));
      // Set stats loading to false after first data is received
      setStatsLoading(false);
    }, (error) => {
      setStatsLoading(false);
    });

    // Cleanup function - unsubscribe from all listeners
    return () => {
      unsubscribeJobs();
      unsubscribeTests();
      unsubscribeCandidates();
      unsubscribeInterviews();
    };
  }, [user, db]);

  // Fetch recent test results for tests created by this HR only
  useEffect(() => {
    if (!user) return;

    const fetchRecentActivity = async () => {
      try {
        // First get all tests created by this HR
        const hrTestsQuery = query(
          collection(db, "hrTests"),
          where("hrId", "==", user.id)
        );
        const hrTestsSnapshot = await getDocs(hrTestsQuery);
        const hrTestIds = hrTestsSnapshot.docs.map(doc => doc.id);

        if (hrTestIds.length === 0) {
          setRecentActivity([]);
          setActivityLoading(false);
          return;
        }

        // Fetch recent completed tests from jobseekerResults for this HR's tests only
        const recentTestsQuery = query(
          collection(db, "jobseekerResults"),
          where("testId", "in", hrTestIds.slice(0, 10)), // Firestore limit
          orderBy("completedAt", "desc"),
          limit(3) // Only show 3 most recent
        );

        const recentTestsSnapshot = await getDocs(recentTestsQuery);
        const activities: any[] = [];

        recentTestsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const completedAt = data.completedAt?.toDate();

          if (completedAt) {
            const timeAgo = getTimeAgo(completedAt);

            activities.push({
              id: doc.id,
              type: "test_completed",
              candidate: data.jobSeekerName || "Unknown Candidate",
              position: data.jobTitle || data.testTitle || "Skills Assessment",
              score: data.totalScore || data.mcqScore || 0,
              time: timeAgo,
              testId: data.testId,
              completedAt: completedAt,
              hasInterview: data.hasCompletedInterview || false,
              interviewScore: data.interviewScore || 0,
            });
          }
        });

        setRecentActivity(activities);
        setActivityLoading(false);
      } catch (error) {
        setRecentActivity([]);
        setActivityLoading(false);
      }
    };

    fetchRecentActivity();
  }, [user, db]);

  // Load HR Gamification Data from Firestore
  useEffect(() => {
    if (!user || statsLoading) return;

    const loadGamificationData = async () => {
      try {

        // Try to fetch existing gamification progress from Firestore
        const progressDoc = await getDoc(doc(db, "hrGamificationProgress", user.id));

        let totalXP = 0;
        let achievements: any[] = [];
        let currentStreak = 0;
        let longestStreak = 0;
        let lastActivityDate = new Date();
        let streakFreezeTokens = 2;

        if (progressDoc.exists()) {
          // User has existing gamification data
          const data = progressDoc.data();
          totalXP = data.totalXP || 0;
          achievements = data.achievements || [];
          currentStreak = data.currentStreak || 0;
          longestStreak = data.longestStreak || 0;
          lastActivityDate = data.lastActivityDate?.toDate() || new Date();
          streakFreezeTokens = data.streakFreezeTokens || 2;

        } else {
          // Initialize new gamification data based on current stats

          // Calculate initial XP based on existing work
          const xpFromJobs = companyStats.totalJobs * HR_XP_POINTS.POST_JOB;
          const xpFromTests = companyStats.totalTests * HR_XP_POINTS.CREATE_TEST;
          const xpFromCandidates = companyStats.totalCandidates * HR_XP_POINTS.REVIEW_CANDIDATE;
          totalXP = xpFromJobs + xpFromTests + xpFromCandidates;

          // Check which achievements should be unlocked based on current stats
          const newAchievements: any[] = [];

          // Job posting achievements
          const jobAchievements = [
            { id: 'first_job', requirement: 1 },
            { id: 'job_poster_5', requirement: 5 },
            { id: 'job_poster_10', requirement: 10 },
            { id: 'job_poster_25', requirement: 25 }
          ];

          jobAchievements.forEach(ach => {
            if (companyStats.totalJobs >= ach.requirement) {
              const achievement = HR_ACHIEVEMENTS.find(a => a.id === ach.id);
              if (achievement) {
                newAchievements.push({
                  achievementId: ach.id,
                  unlockedAt: new Date(),
                  points: achievement.points
                });
                totalXP += achievement.points;
              }
            }
          });

          // Test creation achievements
          const testAchievements = [
            { id: 'first_test', requirement: 1 },
            { id: 'test_master_10', requirement: 10 },
            { id: 'test_master_25', requirement: 25 }
          ];

          testAchievements.forEach(ach => {
            if (companyStats.totalTests >= ach.requirement) {
              const achievement = HR_ACHIEVEMENTS.find(a => a.id === ach.id);
              if (achievement) {
                newAchievements.push({
                  achievementId: ach.id,
                  unlockedAt: new Date(),
                  points: achievement.points
                });
                totalXP += achievement.points;
              }
            }
          });

          // Candidate review achievements
          const candidateAchievements = [
            { id: 'reviewer_10', requirement: 10 },
            { id: 'reviewer_50', requirement: 50 },
            { id: 'reviewer_100', requirement: 100 }
          ];

          candidateAchievements.forEach(ach => {
            if (companyStats.totalCandidates >= ach.requirement) {
              const achievement = HR_ACHIEVEMENTS.find(a => a.id === ach.id);
              if (achievement) {
                newAchievements.push({
                  achievementId: ach.id,
                  unlockedAt: new Date(),
                  points: achievement.points
                });
                totalXP += achievement.points;
              }
            }
          });

          achievements = newAchievements;

          // Save initial gamification data to Firestore
          const initialData = {
            userId: user.id,
            totalXP,
            achievements,
            currentStreak: 0,
            longestStreak: 0,
            lastActivityDate: new Date(),
            streakFreezeTokens: 2,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          await setDoc(doc(db, "hrGamificationProgress", user.id), initialData);
        }

        // Calculate level from XP using helper function
        const levelInfo = getHRLevelByXP(totalXP);

        setHRGamificationData({
          totalXP,
          currentLevel: levelInfo.level,
          currentLevelName: levelInfo.name,
          currentStreak,
          longestStreak,
          lastActivityDate,
          streakFreezeTokens,
          achievements
        });

        setGamificationLoading(false);
      } catch (error) {
        setGamificationLoading(false);
      }
    };

    loadGamificationData();
  }, [user, db, companyStats, statsLoading]);

  // Helper function to calculate time ago
  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;

    return date.toLocaleDateString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "test_completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "interview_scheduled":
        return <Clock className="w-5 h-5 text-blue-600" />;
      case "jd_uploaded":
        return <Upload className="w-5 h-5 text-red-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleEditClick = () => {
    setEditFormData({
      name: profileData?.name || "",
      phone: profileData?.phone || "",
      location: profileData?.location || "",
      company: profileData?.company || "",
      department: profileData?.department || "",
      title: profileData?.title || "",
      bio: profileData?.bio || "",
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
  };

  const handleSaveProfile = async () => {
    if (!user || !editFormData.name.trim()) {
      alert("Name is required");
      return;
    }

    setIsSaving(true);

    try {
      const profileRef = doc(db, "hrProfiles", user.id);

      const updateData = {
        name: editFormData.name,
        phone: editFormData.phone,
        location: editFormData.location,
        company: editFormData.company,
        department: editFormData.department,
        title: editFormData.title,
        bio: editFormData.bio,
        email: profileData?.email,
        joinedDate: profileData?.joinedDate,
        lastUpdated: new Date(),
      };

      await updateDoc(profileRef, updateData);

      // Update local state
      setProfileData({
        ...profileData,
        ...updateData,
      });

      setIsEditing(false);
      setEditFormData({});

      alert("Profile updated successfully!");
    } catch (error) {
      alert("Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 text-white rounded-glass p-8 shadow-glass">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
              <UserIcon className="w-12 h-12 text-white" />
            </div>
            <div>
              {isEditing ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleInputChange}
                    className="text-3xl font-bold bg-white/20 text-white placeholder-white/60 border border-white/30 rounded-lg px-3 py-2 w-full max-w-md focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    placeholder="Enter your name"
                  />
                  <input
                    type="text"
                    name="title"
                    value={editFormData.title}
                    onChange={handleInputChange}
                    className="text-lg bg-white/20 text-red-100 placeholder-white/60 border border-white/30 rounded-lg px-3 py-2 w-full max-w-md focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    placeholder="Enter your job title"
                  />
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-1">
                      <Building className="w-4 h-4 text-red-100" />
                      <input
                        type="text"
                        name="company"
                        value={editFormData.company}
                        onChange={handleInputChange}
                        className="bg-white/20 text-red-100 placeholder-white/60 border border-white/30 rounded px-2 py-1 w-40 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        placeholder="Company"
                      />
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4 text-red-100" />
                      <input
                        type="text"
                        name="location"
                        value={editFormData.location}
                        onChange={handleInputChange}
                        className="bg-white/20 text-red-100 placeholder-white/60 border border-white/30 rounded px-2 py-1 w-40 focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        placeholder="Location"
                      />
                    </div>
                    <div className="flex items-center space-x-1 text-red-100">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {profileData?.joinedDate}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl font-bold">{profileData?.name}</h1>
                  <p className="text-red-100 text-lg">{profileData?.title}</p>
                  <div className="flex items-center space-x-4 mt-2 text-red-100">
                    <div className="flex items-center space-x-1">
                      <Building className="w-4 h-4" />
                      <span>{profileData?.company}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{profileData?.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {profileData?.joinedDate}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {isEditing ? (
            <div className="flex space-x-2">
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="btn-primary disabled:opacity-50"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="btn-secondary text-white border-white/30 hover:border-white/50"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleEditClick}
              className="btn-secondary text-white border-white/30 hover:border-white/50"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Gamification Row: Level Progress + Streak Counter */}
      {!gamificationLoading && hrGamificationData && (
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <RecruiterLevelProgressBar
            currentXP={hrGamificationData.totalXP}
            currentLevel={hrGamificationData.currentLevel}
            levelName={hrGamificationData.currentLevelName}
            animated={true}
            showDetails={true}
          />
          <RecruiterStreakCounter
            currentStreak={hrGamificationData.currentStreak}
            longestStreak={hrGamificationData.longestStreak}
            lastActivityDate={hrGamificationData.lastActivityDate}
            freezeTokens={hrGamificationData.streakFreezeTokens}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* About Section */}
          <div className="glass-card hover:-translate-y-2">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">About</h2>
            {isEditing ? (
              <textarea
                name="bio"
                value={editFormData.bio}
                onChange={handleInputChange}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                placeholder="Tell us about your role, experience, and what you're looking for in candidates..."
              />
            ) : (
              <p className="text-gray-700 leading-relaxed">{profileData?.bio}</p>
            )}
          </div>

          {/* Company Information */}
          <div className="glass-card hover:-translate-y-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-3xl font-semibold text-gray-900">Company Information</h2>
              <Building className="w-5 h-5 text-gray-400" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  Company
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="company"
                    value={editFormData.company}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Company name"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{profileData?.company}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  Department
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="department"
                    value={editFormData.department}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Department"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{profileData?.department}</p>
                )}
              </div>
            </div>
          </div>

          {/* Hiring Statistics */}
          <div className="glass-card hover:-translate-y-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-3xl font-semibold text-gray-900">Hiring Statistics</h2>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {companyStats.totalJobs || 0}
                </div>
                <div className="text-sm text-gray-600">Jobs Posted</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-1">
                  {companyStats.totalTests || 0}
                </div>
                <div className="text-sm text-gray-600">Tests Created</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-1">
                  {companyStats.totalCandidates || 0}
                </div>
                <div className="text-sm text-gray-600">Candidates Reviewed</div>
              </div>
            </div>
          </div>

          {/* Achievements Section */}
          {!gamificationLoading && hrGamificationData && (
            <div className="glass-card hover:-translate-y-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-semibold text-gray-900">Achievements</h2>
                <button
                  onClick={() => setShowAllAchievements(!showAllAchievements)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showAllAchievements ? 'Show Less' : 'View All'}
                </button>
              </div>

              {/* Achievement Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Unlocked</span>
                  <span className="font-semibold">
                    {hrGamificationData.achievements.length} / 24
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
                    style={{ width: `${(hrGamificationData.achievements.length / 24) * 100}%` }}
                  />
                </div>
              </div>

              {/* Achievement Grid Preview or Full Grid */}
              {showAllAchievements ? (
                <HRAchievementGrid
                  userAchievements={hrGamificationData.achievements}
                  showFilters={false}
                  compact={true}
                />
              ) : hrGamificationData.achievements.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {hrGamificationData.achievements.slice(0, 4).map((achievement) => (
                    <div
                      key={achievement.achievementId}
                      className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-4 text-center hover:shadow-md transition-shadow"
                    >
                      <div className="text-4xl mb-2">🏆</div>
                      <p className="text-xs font-semibold text-gray-900 line-clamp-2">
                        Achievement Unlocked
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-5xl mb-3">🏆</div>
                  <p className="text-sm font-medium">No achievements unlocked yet</p>
                  <p className="text-xs mt-1">Keep recruiting to earn achievements!</p>
                </div>
              )}
            </div>
          )}

          {/* Recent Activity */}
          <div className="glass-card hover:-translate-y-2">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-3xl font-semibold text-gray-900">
                Recent Activity
              </h2>
            </div>
            <div className="p-6">
              {activityLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading recent activity...</p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">
                    No recent test results to display.
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Recent test completions will appear here.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        {getActivityIcon(activity.type)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {activity.type === "test_completed" &&
                              `${activity.candidate} completed ${activity.hasInterview ? 'full assessment' : 'test'}`}
                            {activity.type === "interview_scheduled" &&
                              `Interview scheduled with ${activity.candidate}`}
                            {activity.type === "jd_uploaded" &&
                              `Job description uploaded`}
                          </p>
                          <p className="text-sm text-gray-600">
                            {activity.position && `Position: ${activity.position}`}
                            {activity.hasInterview && activity.interviewScore > 0 &&
                              ` • MCQ: ${Math.round(activity.score - activity.interviewScore)}% | Interview: ${Math.round(activity.interviewScore)}%`}
                            {!activity.hasInterview && activity.score !== undefined &&
                              ` • Score: ${Math.round(activity.score)}%`}
                            {activity.hasInterview &&
                              ` • Total: ${Math.round(activity.score)}%`}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-500">{activity.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="glass-card hover:-translate-y-2">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4">
              Contact Information
            </h2>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">{profileData?.email}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Phone number"
                  />
                ) : (
                  <span className="text-gray-700">{profileData?.phone}</span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                {isEditing ? (
                  <input
                    type="text"
                    name="location"
                    value={editFormData.location}
                    onChange={handleInputChange}
                    className="flex-1 border border-gray-300 rounded px-3 py-1 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Location"
                  />
                ) : (
                  <span className="text-gray-700">{profileData?.location}</span>
                )}
              </div>
            </div>
          </div>

          {/* Interviews Completed */}
          <div className="stat-card p-6 hover:-translate-y-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-base text-gray-600">Interviews Completed</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {companyStats.interviewsCompleted || 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          {/* Profile Completion */}
          <div className="glass-card hover:-translate-y-2">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Profile Stats
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Profile Completion</span>
                <span className="text-green-600 font-medium">
                  {profileData?.name && profileData?.company && profileData?.bio ? '100%' : '75%'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ 
                    width: profileData?.name && profileData?.company && profileData?.bio ? '100%' : '75%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRProfile;
